import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { api, eur } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Search, MapPin, Target, Lock, Send, Trash2, Home, Building2, Sparkles } from "lucide-react";

const TYPOLOGIES = ["T0", "T1", "T2", "T3", "T4", "Villa"];
const LOCATIONS = ["Lisboa", "Cascais", "Porto", "Sintra", "Oeiras"];
const PURPOSES = ["Habitação Própria", "Investimento"];
const EMPTY = { typology: "T2", max_budget: "", location: "Lisboa", purpose: "Habitação Própria", notes: "" };

export default function BolsaProcura() {
  const [demands, setDemands] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("");
  const [fLoc, setFLoc] = useState("");
  const [matchDemand, setMatchDemand] = useState(null);

  const load = () => api.get("/demands").then((r) => setDemands(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/demands", { ...form, max_budget: parseFloat(form.max_budget) || 0 });
      toast.success("Procura publicada (cliente anónimo)");
      setModal(false); setForm(EMPTY); load();
    } catch { toast.error("Erro ao publicar"); }
  };

  const propose = async (id, leadId) => {
    try {
      const r = await api.post(`/demands/${id}/propose`, leadId ? { lead_id: leadId } : {});
      toast.success(r.data.message); setMatchDemand(null);
    } catch { toast.error("Erro"); }
  };

  const remove = async (id) => { await api.delete(`/demands/${id}`); toast.success("Removido"); load(); };

  const filtered = demands.filter((d) =>
    (!fType || d.typology === fType) &&
    (!fLoc || d.location === fLoc) &&
    (!q || d.ref_code.toLowerCase().includes(q.toLowerCase()) || d.location.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <Layout title="Bolsa de Procura">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Lock size={15} className="text-[#C9A84C]" /> Compradores partilhados — identidade do cliente confidencial
        </div>
        <button onClick={() => setModal(true)} data-testid="new-demand-btn"
          className="flex items-center gap-2 bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold px-4 py-2.5 rounded-lg transition-all hover:scale-[1.02]">
          <Plus size={18} /> Publicar Procura
        </button>
      </div>

      <div className="card-almar p-4 mb-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className="input-almar pl-9" placeholder="Pesquisar por referência ou localização" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bolsa-search-input" />
        </div>
        <select className="input-almar sm:w-40" value={fType} onChange={(e) => setFType(e.target.value)}>
          <option value="">Todas tipologias</option>
          {TYPOLOGIES.map((t) => <option key={t}>{t}</option>)}
        </select>
        <select className="input-almar sm:w-40" value={fLoc} onChange={(e) => setFLoc(e.target.value)}>
          <option value="">Todas localizações</option>
          {LOCATIONS.map((l) => <option key={l}>{l}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((d) => (
          <div key={d.id} className="card-almar p-5" data-testid={`demand-card-${d.id}`}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                <span className="w-8 h-8 rounded-lg bg-[#C9A84C]/15 flex items-center justify-center text-[#C9A84C]"><Lock size={15} /></span>
                {d.ref_code}
              </span>
              <span className="text-xs px-2 py-1 rounded-full bg-[#C9A84C]/15 text-[#E0C574] border border-[#C9A84C]/30">{d.typology}</span>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-300"><Target size={14} className="text-slate-500" /> Orçamento até <span className="text-[#E0C574] font-semibold">{eur(d.max_budget)}</span></div>
              <div className="flex items-center gap-2 text-slate-300"><MapPin size={14} className="text-slate-500" /> {d.location}</div>
              <div className="flex items-center gap-2 text-slate-300"><Home size={14} className="text-slate-500" /> {d.purpose}</div>
            </div>
            {d.notes && <p className="mt-3 text-xs text-slate-500 italic">"{d.notes}"</p>}
            {!d.is_owner && d.match_count > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2" data-testid={`match-badge-${d.id}`}>
                <Sparkles size={13} /> {d.match_count} {d.match_count === 1 ? "imóvel seu corresponde" : "imóveis seus correspondem"}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-[#1E3A56] flex items-center justify-between">
              <span className="text-[11px] text-slate-500">{d.owner_name ? `Consultor: ${d.owner_name}` : "Consultor confidencial"}</span>
              {d.is_owner ? (
                <button onClick={() => remove(d.id)} className="text-xs text-red-400/70 hover:text-red-400 flex items-center gap-1"><Trash2 size={12} /> Remover</button>
              ) : d.match_count > 0 ? (
                <button onClick={() => setMatchDemand(d)} data-testid={`matches-btn-${d.id}`}
                  className="text-xs flex items-center gap-1 bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold px-3 py-1.5 rounded-lg transition-all">
                  <Building2 size={12} /> Ver correspondências
                </button>
              ) : (
                <button onClick={() => propose(d.id)} data-testid={`propose-btn-${d.id}`}
                  className="text-xs flex items-center gap-1 bg-transparent border border-[#C9A84C] text-[#E0C574] hover:bg-[#C9A84C]/10 px-3 py-1.5 rounded-lg transition-all">
                  <Send size={12} /> Propor Imóvel
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {filtered.length === 0 && <p className="text-slate-500 text-sm mt-8 text-center">Nenhuma procura encontrada.</p>}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <div className="card-almar w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="demand-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-[#C9A84C]">Publicar Procura</h3>
              <button onClick={() => setModal(false)}><X size={20} className="text-slate-400" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-4">A identidade do cliente será ocultada automaticamente.</p>
            <form onSubmit={save} className="space-y-3">
              <select className="input-almar" value={form.typology} onChange={(e) => setForm({ ...form, typology: e.target.value })}>
                {TYPOLOGIES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <input className="input-almar" type="number" placeholder="Orçamento máximo (€)" required data-testid="demand-budget-input"
                value={form.max_budget} onChange={(e) => setForm({ ...form, max_budget: e.target.value })} />
              <select className="input-almar" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}>
                {LOCATIONS.map((l) => <option key={l}>{l}</option>)}
              </select>
              <select className="input-almar" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
                {PURPOSES.map((p) => <option key={p}>{p}</option>)}
              </select>
              <textarea className="input-almar" rows={2} placeholder="Notas (sem dados pessoais)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <button type="submit" data-testid="demand-save-btn" className="w-full bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold py-2.5 rounded-lg transition-all">Publicar</button>
            </form>
          </div>
        </div>
      )}
      {matchDemand && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setMatchDemand(null)}>
          <div className="card-almar w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="match-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl text-[#C9A84C] flex items-center gap-2"><Sparkles size={20} /> Correspondências</h3>
              <button onClick={() => setMatchDemand(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-4">Imóveis da sua carteira que correspondem a {matchDemand.ref_code} · {matchDemand.typology} · até {eur(matchDemand.max_budget)} · {matchDemand.location}</p>
            <div className="space-y-3">
              {matchDemand.matches.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#0D1B2A] border border-[#1E3A56]">
                  <img src={m.property_image || "https://ui-avatars.com/api/?name=" + encodeURIComponent(m.title || "Imovel")} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-100 truncate">{m.title}</div>
                    <div className="text-xs text-slate-500">{m.typology} · {m.location}</div>
                    <div className="text-xs text-[#E0C574]">{eur(m.estimated_value)}</div>
                  </div>
                  <button onClick={() => propose(matchDemand.id, m.id)} data-testid={`propose-match-${m.id}`}
                    className="text-xs flex items-center gap-1 bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold px-3 py-2 rounded-lg transition-all whitespace-nowrap">
                    <Send size={12} /> Propor
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
